import React, { memo, useCallback } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSnackbar } from 'notistack'
import { Button } from 'src/components/shared/Button'
import { DataRow } from 'src/components/shared/DataRow'
import { SectionCard } from 'src/components/shared/SectionCard'
import type { OAuthClient } from 'src/interfaces/oauth'
import { OAuthClientForm } from './oauth-client-form/OAuthClientForm'
import type { OAuthClientFormValues } from './oauth-client-form/OAuthClientForm.interface'
import { GSIcon } from 'src/components/shared/GSIcon'

const CREATE_SUBTITLE =
  "Choose what this customer's API client is allowed to access. The client secret is shown once, immediately after you create it."
const MANAGE_SUBTITLE = "Choose what this customer's API client is allowed to access."
const READ_ONLY_SUBTITLE = "What this customer's API client is allowed to access."

const resolveSubtitle = (client: OAuthClient | null, isReadOnly: boolean): string => {
  if (isReadOnly) {
    return READ_ONLY_SUBTITLE
  }

  if (client === null) {
    return CREATE_SUBTITLE
  }

  return MANAGE_SUBTITLE
}

interface Props {
  client: OAuthClient | null
  customerId: string
  // Without `oauth_clients_manage` (ENG-1207 part 2).
  isReadOnly: boolean
  isSaving: boolean
  onSubmit(values: OAuthClientFormValues): void
  onDirtyChange(isDirty: boolean): void
}

const OAuthClientCardComponent = ({
  client,
  customerId,
  isReadOnly,
  isSaving,
  onSubmit,
  onDirtyChange,
}: Props) => {
  const snackbar = useSnackbar()

  const handleCopyClientId = useCallback(async () => {
    if (!client) {
      return
    }

    if (!navigator.clipboard) {
      snackbar?.enqueueSnackbar('Unable to copy client ID', { variant: 'error' })

      return
    }

    try {
      await navigator.clipboard.writeText(client.clientId)
      snackbar?.enqueueSnackbar('Client ID copied', { variant: 'success' })
    } catch {
      snackbar?.enqueueSnackbar('Unable to copy client ID', { variant: 'error' })
    }
  }, [client, snackbar])

  return (
    <SectionCard title="API client" subtitle={resolveSubtitle(client, isReadOnly)}>
      {client ? (
        <div>
          <DataRow
            title="Client ID"
            direction="column"
            text={client.clientId}
            textClassName={tw`break-all`}
            action={
              <Button
                size="small"
                color="grey"
                iconOnly
                aria-label="Copy client ID"
                leftIcon={
                  <GSIcon
                    iconName="gs_square_behind_square_1"
                    size={20}
                    css={tw`text-shades-1000`}
                  />
                }
                onClick={handleCopyClientId}
                data-testid="oauth-copy-client-id-button"
              />
            }
          />
          {/* The backend keys the client row by partnerId, so these are the same value. Saying
              so stops an admin hunting for a second credential that does not exist. */}
          <Typography variant="body2" css={tw`mt-2 text-shades-400`}>
            This is the same as the customer ID ({customerId}) — the API client is keyed to the
            customer.
          </Typography>
        </div>
      ) : null}
      {/* Read-only with no client there is nothing to show — not a create form that can't submit. */}
      {isReadOnly && client === null ? (
        <Typography variant="body2" css={tw`text-shades-500`} data-testid="oauth-client-none">
          No API client has been created for this customer.
        </Typography>
      ) : (
        <OAuthClientForm
          client={client}
          isReadOnly={isReadOnly}
          isSaving={isSaving}
          onSubmit={onSubmit}
          onDirtyChange={onDirtyChange}
        />
      )}
    </SectionCard>
  )
}

export const OAuthClientCard = memo(OAuthClientCardComponent)
